import React, { useCallback } from 'react';
import {
	Autocomplete,
	AutocompleteChangeReason,
	ListItem,
	Popper,
	TextField,
	TextFieldProps,
	Typography,
} from '@mui/material';
import { Contact, NewContact } from '../../../hooks/useContacts';
import styles from './ContactField.module.scss';
import useErrorScrollContext from '../../../hooks/useErrorScrollContext';
import rpcShared from '@rockpapercoin/rpc-shared';
const {
	contacts: { isArchivedStatus },
} = rpcShared;

// this is fed it's data from the useContact hook
type Option = Contact;

export enum ContactFields {
	firstName = 'firstName',
	lastName = 'lastName',
	email = 'email',
	companyName = 'companyName',
}

export type ContactFieldProps = Pick<TextFieldProps, 'disabled' | 'label'> & {
	contactField: ContactFields;
	options?: Option[];
	contact?: NewContact & { id?: string };
	error: TextFieldProps['helperText'];
	onChange?: ( value: NewContact | undefined ) => void;
	optional?: boolean;
	anchorRef: HTMLDivElement;
	isVisible: boolean;
	onBlur?: () => void;
	descriptiveLabels?: boolean;
	className?: string;
};

export const ContactField: React.FC<ContactFieldProps> = ( {
	contactField,
	options,
	contact,
	disabled,
	error,
	onChange,
	optional,
	anchorRef,
	isVisible,
	onBlur,
	descriptiveLabels = true,
	className,
} ) => {
	const ref = useErrorScrollContext( !!error );
	const label =
		contactField === ContactFields.firstName
			? 'First name'
			: contactField === ContactFields.lastName
				? 'Last name'
				: contactField === ContactFields.companyName
					? 'Company name'
					: 'Email';
	const buildNewContactObjectFromText = useCallback(
		( text: string ): NewContact => {
			// Preserve the id so findContact can locate the existing record by id when
			// the email itself is being changed (email-based lookup would find nothing
			// for a new email address, causing createContactAsVendor instead of updateContact).
			// Preserve customer.id so the signersProps condition in ContractViewSignersAndCounters
			// keeps working even when the user edits a text field after selecting a contact.
			const newContact = {
				id: contact?.id,
				firstName: contact?.firstName,
				lastName: contact?.lastName,
				customer: {
					id: contact?.customer?.id,
					email: contact?.customer?.email,
				},
				companyName: contact?.companyName,
			};
			if ( contactField === ContactFields.email ) {
				newContact.customer.email = text;
			} else if ( contactField === ContactFields.companyName ) {
				newContact.companyName = text;
			} else {
				newContact[ contactField ] = text;
			}
			return newContact;
		},
		[
			contact?.companyName,
			contact?.customer?.email,
			contact?.customer?.id,
			contact?.firstName,
			contact?.id,
			contact?.lastName,
			contactField,
		]
	);
	const selectedOption = options?.find( ( option ) => option.id === contact?.id );
	const inputValue =
		contactField === ContactFields.email
			? contact?.customer?.email || ''
			: contact?.[ contactField ] || '';
	const filterFunction = (
		options: Contact[],
		text: string,
		contactField: ContactFields
	) =>
		!text
			? []
			: options.filter( ( option ) =>
				contactField === ContactFields.email
					? option.customer.email
						.toLowerCase()
						.indexOf( text.toLowerCase() ) !== -1
					: ( option[ contactField ] || '' )
						.toLowerCase()
						.indexOf( text.toLowerCase() ) !== -1
			  );

	const handleInputChange = useCallback(
		( _event: React.SyntheticEvent, value: string, reason: string ) => {
			if ( reason === 'input' ) {
				onChange?.( buildNewContactObjectFromText( value ) );
				return;
			}

			if ( reason === 'clear' ) {
				onChange?.(
					contactField === ContactFields.email
						? undefined
						: buildNewContactObjectFromText( '' )
				);
			}
		},
		[
			buildNewContactObjectFromText,
			contactField,
			onChange
		]
	);

	const handleChange = useCallback(
		(
			_event: React.SyntheticEvent,
			value: Option | string | null,
			reason: AutocompleteChangeReason
		) => {
			if ( typeof value === 'string' ) {
				return;
			}

			if ( reason === 'clear' ) {
				onChange?.(
					contactField === ContactFields.email
						? undefined
						: buildNewContactObjectFromText( '' )
				);
				return;
			}

			onChange?.( value || undefined );
		},
		[
			buildNewContactObjectFromText,
			contactField,
			onChange
		]
	);

	return (
		<form autoComplete='off' className={ className }>
			<Autocomplete<Option, false, boolean, true>
				freeSolo
				autoHighlight
				blurOnSelect
				options={ ( options || [] ).filter(
					( contact ) => !isArchivedStatus( contact.status )
				) }
				disabled={ disabled }
				value={ selectedOption || null }
				inputValue={ inputValue }
				disableClearable={ !optional }
				filterOptions={ ( options, state ) =>
					filterFunction( options, state.inputValue, contactField )
				}
				renderInput={ ( params ) => (
					<TextField
						{ ...params }
						className={ styles.input }
						variant='standard'
						label={
							descriptiveLabels
								? `${ label }${ optional ? ' (optional)' : '' }`
								: label
						}
						error={ !!error }
						helperText={ error }
						onBlur={ onBlur }
						slotProps={ {
							...params.slotProps,
							htmlInput: {
								...params.slotProps.htmlInput,
								'data-cy': contactField,
							},
						} }
					/>
				) }
				onInputChange={ handleInputChange }
				onChange={ handleChange }
				isOptionEqualToValue={ ( option: Option, value: Option | string ) =>
					typeof value === 'string' ? false : option?.id === value?.id
				}
				getOptionLabel={ ( contact ): string =>
					typeof contact === 'string'
						? contact
						: contactField === ContactFields.email
							? contact.customer.email
							: contact[ contactField ] || ''
				}
				renderOption={ ( { key, ...props }, option: Contact ) => (
					<ListItem
						key={ option.id }
						{ ...props }
						className={ [ props.className, styles.suggestion ].join( ' ' ) }
					>
						<Typography className={ styles.name }>
							{ `${ option.firstName } ${ option.lastName }` }
						</Typography>
						<Typography className={ styles.email }>
							{ option.customer.email }
						</Typography>
						{ option.companyName ? (
							<Typography className={ styles.companyName }>
								{ option.companyName }
							</Typography>
						) : null }
					</ListItem>
				) }
				slots={ {
					popper: ( popperProps ) =>
						isVisible ? (
							<Popper
								{ ...popperProps }
								anchorEl={ anchorRef }
								placement='bottom-start'
								className={ styles.popper }
								// so we don't have to disablePortal and can still fill the with of the input container
								sx={ {
									...popperProps.sx,
									minWidth: anchorRef.clientWidth,
									width: anchorRef.clientWidth + 80,
									marginLeft: '-40px',
								} }
							>
								{ popperProps.children }
							</Popper>
						) : null,
				} }
				ref={ ref }
			/>
		</form>
	);
};
